import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  inject,
  signal,
  ViewChild,
  WritableSignal
} from '@angular/core';
import {
  FaqListComponent,
  OrIconComponent,
  PageLayoutComponent,
  SelectableSingleRadioModalComponent,
  TitileNavbarComponent
} from '@ui-components';
import { IonicModule } from '@ionic/angular';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgClass } from '@angular/common';
import {
  AnalyticsService,
  MODAL_TOKEN,
  ModalProvider,
  NAVIGATION_TOKEN,
  NavigationProvider,
  PresentModalComponent,
  ProfileAnalyticsEvent
} from '@core';
import { SendFeedbackCommandHandler } from '../../application/handlers';
import {
  ContactCategory,
  contactCategoriesConst,
  contactTopicsConst,
  hostCitiesConst,
  visibleHostCitiesCount
} from '../../application/utils';
import { Observable, switchMap, take } from 'rxjs';

@Component({
  selector: 'lib-contact',
  templateUrl: './contact.component.html',
  styleUrls: ['./contact.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TitileNavbarComponent,
    IonicModule,
    ReactiveFormsModule,
    NgClass,
    PageLayoutComponent,
    FaqListComponent,
    OrIconComponent
  ],
  providers: [SendFeedbackCommandHandler]
})
export class ContactComponent {
  @ViewChild('citiesBanner') private citiesBanner?: ElementRef<HTMLElement>;

  private readonly sendMessageCommandHandler: SendFeedbackCommandHandler = inject(SendFeedbackCommandHandler);
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private readonly navigationProvider: NavigationProvider = inject(NAVIGATION_TOKEN);
  private readonly analyticsService: AnalyticsService = inject(AnalyticsService);

  readonly minLength: number = 10;
  readonly minLengthErrorVisible: WritableSignal<boolean> = signal<boolean>(false);

  readonly categories: ContactCategory[] = contactCategoriesConst;
  readonly visibleCities: string[] = hostCitiesConst.slice(0, visibleHostCitiesCount);
  readonly hiddenCities: string[] = hostCitiesConst.slice(visibleHostCitiesCount);

  readonly showCitiesTooltip: WritableSignal<boolean> = signal<boolean>(false);

  readonly selectedTopic: WritableSignal<string | null> = signal<string | null>(null);

  readonly contactForm: FormGroup = new FormGroup({
    topic: new FormControl('', [Validators.required]),
    message: new FormControl('', [Validators.required, Validators.minLength(this.minLength), Validators.maxLength(500)])
  });

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.citiesBanner?.nativeElement.contains(event.target as Node)) {
      this.showCitiesTooltip.set(false);
    }
  }

  toggleCitiesTooltip(): void {
    this.showCitiesTooltip.update((visible: boolean) => !visible);
  }

  openTopicPicker(): void {
    this.modalProvider
      .showModal$<string>({
        component: SelectableSingleRadioModalComponent<string>,
        cssClass: 'present-modal',
        componentProps: {
          buttonText: 'Wybierz',
          header: 'Temat rozmowy',
          providedData: contactTopicsConst,
          selectedData: this.selectedTopic()
        }
      })
      .pipe(take(1))
      .subscribe((topic: string | undefined) => {
        if (!topic) return;

        this.selectedTopic.set(topic);
        this.contactForm.get('topic')?.setValue(topic);
      });
  }

  sendMessage(): void {
    if (this.contactForm.invalid) {
      this.minLengthErrorVisible.set(true);
      return;
    }

    this.minLengthErrorVisible.set(false);
    this.analyticsService.buttonClick(ProfileAnalyticsEvent.ContactMessageSent, {
      topic: this.contactForm.get('topic')?.value
    });

    this.sendMessageCommandHandler
      .send(this.buildMessage())
      .pipe(
        take(1),
        switchMap(() => this.showSuccessMessage()),
        take(1)
      )
      .subscribe(() => this.navigationProvider.back());
  }

  private buildMessage(): string {
    const { topic, message } = this.contactForm.value;

    return `Temat: ${topic}\n\n${message}`;
  }

  private showSuccessMessage(): Observable<void> {
    return this.modalProvider.showModal$({
      component: PresentModalComponent,
      componentProps: {
        header: 'Wiadomość wysłana',
        message: 'Dziękujemy za kontakt! Odpowiemy tak szybko jak to możliwe.',
        btnTxt: 'Zamknij'
      },
      cssClass: 'present-modal'
    });
  }
}
